import { apiClient } from '@/utils/api-client';

// Types for Portal Roles and Modules
export interface PortalModule {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PortalModuleSummary {
  id: string;
  name: string;
  display_name: string;
}

export interface PortalRole {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  is_default: boolean;
  sort_order: number;
  modules: PortalModuleSummary[];
  created_at: string;
  updated_at: string;
}

interface PortalModuleList {
  items: PortalModule[];
  total: number;
}

interface PortalRoleList {
  items: PortalRole[];
  total: number;
}

export interface PortalRoleCreateData {
  name: string;
  display_name: string;
  description?: string;
  sort_order?: number;
  is_default?: boolean;
  module_ids?: string[];
}

export interface PortalRoleUpdateData {
  display_name?: string;
  description?: string;
  sort_order?: number;
  is_default?: boolean;
  module_ids?: string[];
}

interface PortalModuleCreateData {
  name: string;
  display_name: string;
  description?: string;
  sort_order?: number;
}

interface PortalModuleUpdateData {
  display_name?: string;
  description?: string;
  sort_order?: number;
}

class PortalRoleService {
  // Module management methods
  async getModules(params?: {
    include_inactive?: boolean;
  }): Promise<PortalModuleList> {
    return apiClient.get<PortalModuleList>('/investor-portal-access/modules', {
      params,
    });
  }

  async getModuleById(id: string): Promise<PortalModule> {
    return apiClient.get<PortalModule>(`/investor-portal-access/modules/${id}`);
  }

  async createModule(data: PortalModuleCreateData): Promise<PortalModule> {
    return apiClient.post<PortalModule>(
      '/investor-portal-access/modules',
      data
    );
  }

  async updateModule(
    id: string,
    data: PortalModuleUpdateData
  ): Promise<PortalModule> {
    return apiClient.put<PortalModule>(
      `/investor-portal-access/modules/${id}`,
      data
    );
  }

  async deleteModule(id: string): Promise<void> {
    return apiClient.delete(`/investor-portal-access/modules/${id}`);
  }

  // Role management methods
  async getRoles(params?: {
    include_system?: boolean;
    include_inactive?: boolean;
  }): Promise<PortalRoleList> {
    return apiClient.get<PortalRoleList>('/investor-portal-access/roles', {
      params,
    });
  }

  async getRoleById(id: string): Promise<PortalRole> {
    return apiClient.get<PortalRole>(`/investor-portal-access/roles/${id}`);
  }

  async createRole(data: PortalRoleCreateData): Promise<PortalRole> {
    return apiClient.post<PortalRole>('/investor-portal-access/roles', data);
  }

  async updateRole(
    id: string,
    data: PortalRoleUpdateData
  ): Promise<PortalRole> {
    return apiClient.put<PortalRole>(
      `/investor-portal-access/roles/${id}`,
      data
    );
  }

  async deleteRole(id: string): Promise<void> {
    return apiClient.delete(`/investor-portal-access/roles/${id}`);
  }

  // Helper method to update role modules
  async updateRoleModules(
    roleId: string,
    moduleIds: string[]
  ): Promise<PortalRole> {
    return this.updateRole(roleId, { module_ids: moduleIds });
  }
}

export const portalRoleService = new PortalRoleService();
